import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '../common/firebase/Firebase';
import { logEvent } from 'firebase/analytics';

export const useAnalytics = () => {
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  
  // location.pathname이 변경될 때마다 ref 업데이트
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  const trackPageView = useCallback((customPageTitle?: string) => {
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_title: customPageTitle || document.title,
        page_location: window.location.href,
        page_path: locationRef.current,
        page_referrer: document.referrer || 'direct',
        timestamp: new Date().toISOString()
      });
      //console.log(`📊 Google Analytics: 페이지 뷰 추적 - ${customPageTitle || locationRef.current}`);
    }
  }, []); // 의존성 없음으로 함수 재생성 방지

  const trackButtonClick = useCallback((
    buttonName: string,
    buttonId?: string,
    additionalParams?: Record<string, any>
  ) => {
    if (analytics) {
      const eventData = {
        button_name: buttonName,
        button_id: buttonId || 'no-id',
        page_path: locationRef.current,
        timestamp: new Date().toISOString(),
        ...additionalParams
      };
      
      try {
        logEvent(analytics, 'button_click', eventData);
        //console.log(`📊 Google Analytics: 버튼 클릭 추적 - ${buttonName} (${buttonId || 'no-id'})`);

      } catch (error) {
        console.error('❌ Analytics 이벤트 전송 실패:', error);
      }
    }
  }, []);

  const trackCustomEvent = useCallback((
    eventName: string,
    eventParams: Record<string, any>
  ) => {
    if (analytics) {
      logEvent(analytics, eventName, {
        page_path: locationRef.current,
        timestamp: new Date().toISOString(),
        ...eventParams
      });
      //console.log(`📊 Google Analytics: 커스텀 이벤트 추적 - ${eventName}`, eventParams);
    }
  }, [locationRef.current]);

  const trackVideoPlay = useCallback((
    videoTitle: string,
    videoId?: string,
    videoDuration?: number
  ) => {
    if (analytics) {
      logEvent(analytics, 'video_play', {
        video_title: videoTitle,
        video_id: videoId || 'no-id',
        video_duration: videoDuration || 0,
        page_path: locationRef.current,
        timestamp: new Date().toISOString()
      });
      //console.log(`�� Google Analytics: 비디오 재생 추적 - ${videoTitle} (${videoId || 'no-id'})`);
    }
  }, [locationRef.current]);

  const trackScrollDepth = useCallback((
    scrollDepth: number,
    scrollDirection: 'up' | 'down'
  ) => {
    if (analytics) {
      logEvent(analytics, 'scroll_depth', {
        scroll_depth: scrollDepth,
        scroll_direction: scrollDirection,
        page_path: locationRef.current,
        timestamp: new Date().toISOString()
      });
    }
  }, [locationRef.current]);

  const trackSectionView = useCallback((
    sectionName: string,
    sectionId?: string
  ) => {
    if (analytics) {
      logEvent(analytics, 'section_view', {
        section_name: sectionName,
        section_id: sectionId || 'no-id',
        page_path: locationRef.current,
        timestamp: new Date().toISOString()
      });
      //console.log(`📊 Google Analytics: 섹션 뷰 추적 - ${sectionName} (${sectionId || 'no-id'})`);
    }
  }, [locationRef.current]);

  return {
    trackPageView,
    trackButtonClick,
    trackCustomEvent,
    trackVideoPlay,
    trackScrollDepth,
    trackSectionView
  };
};
